/**
 * useRecordsData - Data fetching hook for records
 *
 * Uses TanStack Query useInfiniteQuery for infinite scroll.
 * Accumulates pages as user scrolls, org-aware via queryKey.
 */

import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { RecordScriptStepFilterEcho } from '@aica/api-types/schemas';
import { useRole } from '@/contexts/RoleContext';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useDemoFilter } from '@/features/demo/useDemoFilter';
import type { ApiClient } from '@/lib/api';
import { ApiError } from '@/lib/fetch-utils';
import { isUnknownOutcomeRefusal } from '@/lib/funnel-missed';
import {
  isUnknownScriptStepRefusal,
  parseScriptStepFilter,
  scriptStepEchoFor,
} from '@/lib/records/script-step-filter';
import { toRecordItems, type RecordItem } from '@/lib/api/mappers';
import {
  nextRecordsPageParam,
  recordsSearchDeeperState,
  recordsSearchDegraded,
  recordsTotalState,
  type RecordsPageParam,
} from './records-paging';
import { recordsFilterParams, type RecordsServerFilters } from './records-filter-params';

interface UseRecordsDataOptions {
  itemsPerPage?: number;
  seriesId?: string;
  serverFilters?: RecordsServerFilters;
  /**
   * The page shows something else instead (semantic results, #1234): the
   * list and its count are not fetched on each filter change only to be
   * thrown away.
   */
  paused?: boolean;
}

export interface UseRecordsDataReturn {
  // Data
  records: RecordItem[];
  totalRecords: number;
  /**
   * До скольких символов сервер укоротил поисковый запрос; null — не укорачивал.
   *
   * Предел D1 на длину шаблона LIKE меряется в байтах, поэтому в символах он
   * зависит от языка — число приходит с сервера, а не берётся из константы.
   * Показать пометку обязательно: обрезанный запрос возвращает надмножество,
   * и молчание превратило бы громкий отказ в тихий неверный ответ.
   */
  searchTruncatedTo: number | null;
  /**
   * `totalRecords` — нижняя граница, а не полное число совпадений (#930).
   * Показать пометку обязательно: иначе пользователь примет «не меньше N» за N.
   */
  searchTotalIsPartial: boolean;
  /**
   * Часть выдачи дал запасной поиск D1, потому что Postgres был недоступен
   * (#1035): регистр букв вне A–Z в ней учитывается. Показать пометку.
   */
  searchDegraded: boolean;
  /**
   * Поиск идёт глубже (#1048): `searching` — хук сам догружает короткие
   * страницы с курсором; `stalled` — предел автодогрузки исчерпан, показать
   * кнопку «Искать дальше» (`fetchNextPage`); `null` — ни то ни другое.
   * Пустой список при `searching`/`stalled` — НЕ «ничего не найдено».
   */
  searchDeeper: 'searching' | 'stalled' | null;
  /**
   * Шаг, который сервер применил фильтром `scriptStep` (#879), с именем для
   * чипа. Нет ответа списка (загрузка, ошибка, пауза) — нет и имени; кому он
   * нужен, сверяет `stepId` со своим фильтром.
   */
  scriptStepFilter?: RecordScriptStepFilterEcho;
  /**
   * Сервер отверг шаг фильтра `scriptStep` как неизвестный (#879): шага нет в
   * скрипте организации — его удалили после того, как фильтр был сохранён.
   * Ошибка и ключ запроса — один и тот же, поэтому признак всегда про шаг,
   * с которым запрос ушёл; пока идёт повтор, признака нет. Любой другой отказ
   * списка (в том числе иной 400) признаком не является.
   */
  scriptStepRejected: boolean;
  /**
   * Сервер отверг исход `outcome` запроса как неизвестный (400): так отвечает
   * edge-api, откатанный за живым бандлом, на корзину, которой он не знает
   * («Пропущенные»). Признак — про исход, с которым запрос ушёл; пока идёт
   * повтор или исхода в запросе нет — его нет. Любой другой отказ списка
   * признаком не является.
   */
  outcomeRefused: boolean;
  /**
   * Список ответил на запрос с фильтром `scriptStep` (#879), но не подтвердил
   * его: в первой странице нет эха `scriptStepFilter` или оно называет другой
   * шаг. Так отвечает сервер старше фильтра (откат edge-api при новом
   * бандле): незнакомый параметр он молча отбрасывает и отдаёт полный список —
   * под чипом «применён» он был бы неправдой. Применивший фильтр сервер эхо
   * кладёт всегда, и в пустой ответ тоже. У запроса нет placeholderData, поэтому
   * `data` — всегда про шаг, с которым запрос ушёл. Нет ответа или фильтр не
   * разбирается — признака нет.
   */
  scriptStepIgnored: boolean;
  /**
   * Просили разговоры лида сделки (`includeSourceLead`, #745), а сервер не
   * смог их прочитать хотя бы для одной загруженной страницы: в выдаче —
   * разговоры одной сделки. Показать пометку, иначе пропажа лида молчалива.
   */
  sourceLeadsUnavailable: boolean;

  // State
  loading: boolean;
  isRefreshing: boolean;
  /** Epoch ms of the last successful fetch (first page), or 0 before the first load. */
  dataUpdatedAt: number;
  error: string | null;
  /** The server refused the read (401/403): `records` is empty, the cached pages are not handed out. */
  readRefused: boolean;

  // Infinite scroll
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** `error` comes from «Show more», not from a re-read of the pages held. */
  isFetchNextPageError: boolean;

  /**
   * The demo scope the list was asked for — the semantic search sends the
   * same one, so both show the same records (#1234).
   */
  includeDemo?: 'yes' | 'no';

  // Actions
  refetchRecords: () => Promise<void>;
}

export function useRecordsData(
  options: UseRecordsDataOptions = {}
): UseRecordsDataReturn {
  const { selectedOrgId } = useRole();
  const { apiClient } = useAuth();
  const { includeDemo } = useDemoFilter();
  return useRecordsList({ ...options, apiClient, selectedOrgId, includeDemo });
}

/**
 * Список записей порциями — без контекстов портала (#1067).
 *
 * Клиент API и организацию передаёт вызывающий: встроенное приложение Bitrix24
 * работает без `UnifiedAuthContext` (Clerk) и берёт их из своих хуков. Правила
 * поиска порциями — курсор, конец по несдвинувшемуся курсору, автодогрузка
 * коротких страниц, пометки — одни на портал и встроенное приложение.
 */
export function useRecordsList(
  options: UseRecordsDataOptions & {
    apiClient: ApiClient;
    selectedOrgId: string | null;
    /** Нет значения — параметр не отправляется, решает сервер. */
    includeDemo?: 'yes' | 'no';
  }
): UseRecordsDataReturn {
  const {
    itemsPerPage = 50,
    seriesId,
    serverFilters,
    paused = false,
    apiClient,
    selectedOrgId,
    includeDemo,
  } = options;
  const { t } = useTranslation(['records', 'common']);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    dataUpdatedAt,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: [
      'records',
      'infinite',
      selectedOrgId,
      { limit: itemsPerPage, seriesId, includeDemo, ...serverFilters },
    ] as const,
    queryFn: async ({ pageParam }: { pageParam: RecordsPageParam }) => {
      const result = await apiClient.records.getRecords({
        limit: String(itemsPerPage),
        offset: String(pageParam.offset),
        ...(pageParam.searchCursor ? { searchCursor: pageParam.searchCursor } : {}),
        ...recordsFilterParams(serverFilters, { includeDemo, seriesId }),
        ...(serverFilters?.includeSourceLead ? { includeSourceLead: 'true' } : {}),
        ...(serverFilters?.search ? { search: serverFilters.search } : {}),
      });
      return result;
    },
    initialPageParam: { offset: 0 } as RecordsPageParam,
    getNextPageParam: (lastPage, allPages) =>
      nextRecordsPageParam(lastPage, allPages, { singlePage: Boolean(seriesId) }),
    enabled: !!selectedOrgId && !paused,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // The server refused the read itself (401/403): TanStack still holds the
  // pages of the last good answer, and nothing derived from them — rows, the
  // count, search marks, the next page — may outlive the refusal.
  const readRefused =
    error instanceof ApiError && (error.status === 401 || error.status === 403);
  const pages = readRefused ? undefined : data?.pages;
  const morePages = !readRefused && (hasNextPage ?? false);

  // Without useMemo, toRecordItems re-runs over EVERY accumulated page on each
  // render (record selection, hover, any state change in RecordsPage), and the
  // fresh array identity cascades into filteredRecords + the option-accumulator
  // effects. With deep infinite-scroll this blocks the main thread on record
  // switch. Keyed on `data` so mapping runs only when a page is actually fetched.
  const allRecords = useMemo(
    () => pages?.flatMap((page) => toRecordItems(page.records, t)) ?? [],
    [pages, t]
  );
  // Счёт и его полнота — с последней страницы: при поиске порциями глубже
  // просмотрено больше, см. `recordsTotalState`.
  const { total: totalRecords, partial: searchTotalIsPartial } = recordsTotalState(pages);
  // На паузе (вместо списка — семантическая выдача, #1234) страницы в кеше —
  // от прежнего текстового поиска: их пометки описывали бы чужую выдачу, а
  // автодогрузка тянула бы список, который не показан (`enabled: false` не
  // останавливает `fetchNextPage`). Состояние поиска на паузе — нейтральное.
  const searchPages = paused ? undefined : pages;
  // Первая страница: обрезка — свойство запроса, у всех страниц она одна.
  const searchTruncatedTo = searchPages?.[0]?.searchTruncatedTo ?? null;
  const searchDegraded = recordsSearchDegraded(searchPages);

  // Короткая страница с курсором не даёт прокрутки, и подгрузка по скроллу
  // её бы не продолжила: догружаем сами, до предела (#1048).
  const deeperState = recordsSearchDeeperState(searchPages, {
    hasNextPage: morePages,
    pageSize: itemsPerPage,
    fetchNextPageFailed: isFetchNextPageError,
  });
  // `!isFetching`, а не только `!isFetchingNextPage`: догрузка посреди полной
  // перезагрузки отменила бы её (`cancelRefetch`) и пошла бы от старых страниц.
  useEffect(() => {
    if (deeperState === 'continue' && !isFetching) void fetchNextPage();
  }, [deeperState, isFetching, fetchNextPage]);
  const askedScriptStepParam = serverFilters?.scriptStep;
  const askedScriptStep = useMemo(
    () => parseScriptStepFilter(askedScriptStepParam),
    [askedScriptStepParam]
  );
  const scriptStepEcho = pages?.[0]?.scriptStepFilter;
  // Подтверждение — на каждой странице: страница — свой запрос, и сервер,
  // откатившийся между подгрузками, молча отбросил бы параметр и дописал под
  // чип неотфильтрованные записи.
  const scriptStepIgnored =
    askedScriptStep !== null &&
    pages !== undefined &&
    pages.some(
      (page) => scriptStepEchoFor(askedScriptStep, page.scriptStepFilter) === null
    );
  const searchDeeper =
    deeperState === 'continue'
      ? 'searching'
      : deeperState === 'stalled'
        ? 'stalled'
        : null;

  return {
    records: allRecords,
    includeDemo,
    totalRecords,
    searchTruncatedTo,
    searchTotalIsPartial,
    searchDegraded,
    searchDeeper,
    // Имя шага — с первой страницы: у страниц, подтвердивших шаг, оно одно.
    scriptStepFilter: scriptStepEcho,
    scriptStepRejected: !isFetching && isUnknownScriptStepRefusal(error),
    outcomeRefused:
      !isFetching &&
      serverFilters?.outcome !== undefined &&
      isUnknownOutcomeRefusal(error),
    scriptStepIgnored,
    sourceLeadsUnavailable:
      pages?.some((page) => page.sourceLeadsUnavailable === true) ?? false,
    loading: isLoading,
    isRefreshing: isFetching && !isLoading && !isFetchingNextPage,
    dataUpdatedAt,
    error: error
      ? error instanceof Error
        ? error.message
        : t('records.failedToFetch')
      : null,
    readRefused,
    fetchNextPage: () => void fetchNextPage(),
    hasNextPage: morePages,
    isFetchingNextPage,
    // `error` with rows on screen is either «Show more» that failed or a
    // re-read of the pages held that failed — the two read differently.
    isFetchNextPageError,
    refetchRecords: async () => {
      // Reset to first page only, then refetch — avoids re-fetching all loaded pages
      await queryClient.resetQueries({
        queryKey: ['records', 'infinite', selectedOrgId],
      });
    },
  };
}
